import { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Clock,
  CheckCircle,
  XCircle,
  User,
  Calendar,
  Award,
  MessageSquare,
  Shield,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { attemptsApi, examsApi } from '@/api/endpoints';
import { Question, QuestionOption } from '@/types';
import { format } from 'date-fns';

type FilterType = 'all' | 'correct' | 'wrong' | 'unanswered';
const PER_PAGE = 10;

export const StudentGradeDetailPage = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const [searchParams] = useSearchParams();
  const examId = searchParams.get('exam') || '';
  const studentName = searchParams.get('name') || '';
  const studentEmail = searchParams.get('email') || '';
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>('all');
  const [page, setPage] = useState(1);

  const { data: resultRes, isLoading } = useQuery({
    queryKey: ['attempt-result', attemptId],
    queryFn: () => attemptsApi.getResult(attemptId!),
    enabled: !!attemptId,
  });

  const result = resultRes?.data?.data;
  const attempt = result?.attempt as any;
  const answers: any[] = result?.answers || [];

  const { data: examRes } = useQuery({
    queryKey: ['exam-detail', examId || attempt?.exam_id],
    queryFn: () => examsApi.get(examId || attempt?.exam_id),
    enabled: !!(examId || attempt?.exam_id),
  });

  const exam = examRes?.data?.data;

  const { data: questionsRes } = useQuery({
    queryKey: ['exam-questions', examId || attempt?.exam_id],
    queryFn: () => examsApi.getQuestions(examId || attempt!.exam_id),
    enabled: !!(examId || attempt?.exam_id),
  });

  const allQuestions: Question[] = Array.isArray(questionsRes?.data?.data) ? questionsRes.data.data : [];

  // Only show questions the student actually had (matched by answers)
  const questionMap = allQuestions.reduce((acc: Record<string, Question>, q) => {
    acc[q.id] = q;
    return acc;
  }, {});

  // Build the question list: answered questions + their details
  const attemptQuestions = useMemo(() => {
    return answers.map((ans) => ({
      question: questionMap[ans.question_id],
      answer: ans,
    }));
  }, [answers, questionMap]);

  // Compute stats from actual attempt data only
  const correctCount = answers.filter((a) => a.is_correct === true).length;
  const wrongCount = answers.filter((a) => a.is_correct === false).length;
  const unansweredCount = attemptQuestions.filter((q) => !q.answer || (q.answer.answer === null && q.answer.code_answer === null)).length;
  const totalAnswered = answers.length;

  // Apply filter
  const filteredQuestions = useMemo(() => {
    switch (filter) {
      case 'correct':
        return attemptQuestions.filter((q) => q.answer?.is_correct === true);
      case 'wrong':
        return attemptQuestions.filter((q) => q.answer?.is_correct === false);
      case 'unanswered':
        return attemptQuestions.filter((q) => !q.answer || (q.answer.answer === null && q.answer.code_answer === null));
      default:
        return attemptQuestions;
    }
  }, [attemptQuestions, filter]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredQuestions.length / PER_PAGE));
  const paginatedQuestions = filteredQuestions.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // Reset page when filter changes
  const handleFilterChange = (f: FilterType) => {
    setFilter(f);
    setPage(1);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  if (!attempt) {
    return (
      <div className="text-center py-20">
        <p className="text-white/50">Attempt not found</p>
        <button onClick={() => navigate(-1)} className="mt-4 btn-outline px-4 py-2 rounded-lg text-sm">Go Back</button>
      </div>
    );
  }

  // Use actual question points sum, not the exam's configured total_marks
  const actualTotalPoints = allQuestions.length > 0
    ? allQuestions.reduce((sum, q) => sum + (q.points || 0), 0)
    : exam?.total_marks || attempt.max_score || 100;
  const score = attempt.score || 0;
  const percentage = actualTotalPoints > 0 ? (score / actualTotalPoints) * 100 : 0;
  // passing_marks is stored as raw score based on total_marks, convert to percentage for comparison
  const passingPercentage = (exam?.total_marks ?? 0) > 0 ? ((exam?.passing_marks || 0) / (exam?.total_marks ?? 1)) * 100 : 0;
  const passed = percentage >= passingPercentage;
  const grade = calculateGrade(percentage);

  const timeTaken = attempt.end_time && attempt.start_time
    ? Math.floor((new Date(attempt.end_time).getTime() - new Date(attempt.start_time).getTime()) / 1000)
    : 0;
  const mins = Math.floor(timeTaken / 60);
  const secs = timeTaken % 60;

  const filters: { key: FilterType; label: string; count: number; color: string }[] = [
    { key: 'all', label: 'All', count: attemptQuestions.length, color: 'text-white/70' },
    { key: 'correct', label: 'Correct', count: correctCount, color: 'text-emerald-400' },
    { key: 'wrong', label: 'Wrong', count: wrongCount, color: 'text-red-400' },
    { key: 'unanswered', label: 'Unanswered', count: unansweredCount, color: 'text-amber-400' },
  ];

  return (
    <div className="space-y-5">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-white/[0.06] text-white/60 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white/90">{exam?.title || 'Exam Results'}</h1>
            <p className="text-xs text-white/40 mt-0.5">Student Grade Detail</p>
          </div>
        </div>
        <div className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${
          passed ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' : 'bg-red-500/15 text-red-400 border border-red-500/20'
        }`}>
          {passed ? 'PASSED' : 'FAILED'} &middot; {grade}
        </div>
      </div>

      {/* Compact summary strip */}
      <div className="card-dark px-5 py-4">
        <div className="flex items-center justify-between flex-wrap gap-y-3">
          {/* Student */}
          <div className="flex items-center space-x-3 min-w-[200px]">
            <div className="w-9 h-9 rounded-full bg-teal-500/15 flex items-center justify-center">
              <User className="w-4 h-4 text-teal-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-white/90 leading-tight">{studentName || attempt.student_name || attempt.student_id}</p>
              <p className="text-xs text-white/40">{studentEmail || attempt.student_email || ''}</p>
            </div>
          </div>

          {/* Inline stats */}
          <div className="flex items-center space-x-6 text-sm">
            <div className="text-center">
              <p className="text-lg font-bold text-white/90">{score}<span className="text-white/30 text-sm font-normal">/{actualTotalPoints}</span></p>
              <p className="text-[10px] text-white/40 uppercase tracking-wider">Score</p>
            </div>
            <div className="w-px h-8 bg-white/[0.08]" />
            <div className="text-center">
              <p className={`text-lg font-bold ${passed ? 'text-emerald-400' : 'text-red-400'}`}>{percentage.toFixed(1)}%</p>
              <p className="text-[10px] text-white/40 uppercase tracking-wider">Percentage</p>
            </div>
            <div className="w-px h-8 bg-white/[0.08]" />
            <div className="text-center">
              <p className="text-lg font-bold text-emerald-400">{correctCount}<span className="text-white/30 text-sm font-normal">/{totalAnswered}</span></p>
              <p className="text-[10px] text-white/40 uppercase tracking-wider">Correct</p>
            </div>
            <div className="w-px h-8 bg-white/[0.08]" />
            <div className="text-center">
              <p className="text-lg font-bold text-red-400">{wrongCount}</p>
              <p className="text-[10px] text-white/40 uppercase tracking-wider">Wrong</p>
            </div>
            <div className="w-px h-8 bg-white/[0.08]" />
            <div className="flex items-center space-x-1.5 text-white/50">
              <Clock className="w-3.5 h-3.5" />
              <span className="text-sm">{mins}m {secs}s</span>
            </div>
            <div className="flex items-center space-x-1.5 text-white/50">
              <Calendar className="w-3.5 h-3.5" />
              <span className="text-sm">{attempt.end_time ? format(new Date(attempt.end_time), 'MMM d, h:mm a') : 'N/A'}</span>
            </div>
            {(attempt.violation_count > 0 || (attempt.violations?.length > 0)) && (
              <div className="flex items-center space-x-1.5 text-amber-400">
                <Shield className="w-3.5 h-3.5" />
                <span className="text-sm">{attempt.violation_count || attempt.violations?.length}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Feedback */}
      {attempt.feedback && (
        <div className="card-dark px-5 py-3 flex items-start space-x-3">
          <MessageSquare className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Instructor Feedback</p>
            <p className="text-sm text-white/80 leading-relaxed">{attempt.feedback}</p>
          </div>
        </div>
      )}

      {/* Filter tabs + pagination header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-1 bg-white/[0.03] border border-white/[0.06] rounded-lg p-1">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => handleFilterChange(f.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filter === f.key
                  ? 'bg-white/[0.1] text-white shadow-sm'
                  : 'text-white/50 hover:text-white/70 hover:bg-white/[0.04]'
              }`}
            >
              {f.label}
              <span className={`ml-1.5 ${filter === f.key ? f.color : 'text-white/30'}`}>{f.count}</span>
            </button>
          ))}
        </div>
        <span className="text-xs text-white/40">
          {filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''} &middot; Page {page}/{totalPages}
        </span>
      </div>

      {/* Questions */}
      <div className="space-y-3">
        {paginatedQuestions.length === 0 ? (
          <div className="card-dark p-8 text-center">
            <AlertCircle className="w-8 h-8 text-white/20 mx-auto mb-2" />
            <p className="text-sm text-white/40">No questions match this filter</p>
          </div>
        ) : (
          paginatedQuestions.map((item, pageIdx) => {
            const { question, answer } = item;
            const globalIdx = (page - 1) * PER_PAGE + pageIdx;
            const originalIdx = attemptQuestions.indexOf(item);
            const isCorrect = answer?.is_correct;
            const pointsAwarded = answer?.points_awarded ?? answer?.points_earned ?? answer?.score ?? 0;
            const maxPoints = question?.points || answer?.max_score || 0;

            return (
              <div key={answer?.question_id || globalIdx} className="card-dark overflow-hidden">
                {/* Question header bar */}
                <div className={`px-5 py-3 flex items-center justify-between border-b ${
                  !answer ? 'border-white/[0.04] bg-white/[0.01]' :
                  isCorrect ? 'border-emerald-500/10 bg-emerald-500/[0.03]' : 'border-red-500/10 bg-red-500/[0.03]'
                }`}>
                  <div className="flex items-center space-x-3">
                    <span className={`w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold ${
                      !answer ? 'bg-white/[0.06] text-white/40' :
                      isCorrect ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {originalIdx + 1}
                    </span>
                    <div className="flex items-center space-x-2">
                      {question && (
                        <>
                          <span className="px-1.5 py-0.5 rounded text-[10px] bg-white/[0.06] text-white/40 uppercase">
                            {question.question_type?.replace('_', ' ')}
                          </span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] bg-white/[0.06] text-white/40 uppercase">
                            {question.difficulty || 'medium'}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {answer && (
                      <span className={`flex items-center space-x-1 text-xs font-medium ${
                        isCorrect ? 'text-emerald-400' : 'text-red-400'
                      }`}>
                        {isCorrect ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                        <span>{isCorrect ? 'Correct' : 'Incorrect'}</span>
                      </span>
                    )}
                    {!answer && (
                      <span className="flex items-center space-x-1 text-xs font-medium text-amber-400">
                        <AlertCircle className="w-3.5 h-3.5" />
                        <span>Unanswered</span>
                      </span>
                    )}
                    <span className={`text-sm font-bold ${
                      !answer ? 'text-white/30' : isCorrect ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {pointsAwarded}/{maxPoints} <span className="text-[10px] font-normal text-white/30">pts</span>
                    </span>
                  </div>
                </div>

                {/* Question body */}
                <div className="px-5 py-4">
                  <p className="text-sm text-white/85 leading-relaxed mb-3">
                    {question?.question_text || `Question ID: ${answer?.question_id}`}
                  </p>

                  {/* MCQ Options */}
                  {question?.options && question.options.length > 0 && (
                    <div className="space-y-1.5 mb-2">
                      {question.options.map((option: QuestionOption, optIdx: number) => {
                        const isCorrectAnswer = question.correct_answer?.includes(option.text) || (option.id && question.correct_answer?.includes(option.id));
                        const isStudentAnswer = answer?.answer?.includes(option.text) || (option.id && answer?.answer?.includes(option.id));

                        return (
                          <div
                            key={option.id || optIdx}
                            className={`px-3 py-2 rounded-md border text-sm flex items-center justify-between ${
                              isCorrectAnswer && isStudentAnswer
                                ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300'
                                : isStudentAnswer
                                ? 'bg-red-500/10 border-red-500/25 text-red-300'
                                : isCorrectAnswer
                                ? 'bg-teal-500/5 border-teal-500/20 text-teal-300'
                                : 'bg-transparent border-white/[0.04] text-white/50'
                            }`}
                          >
                            <span>
                              <span className="font-mono mr-2 text-white/30 text-xs">{String.fromCharCode(65 + optIdx)}</span>
                              {option.text}
                            </span>
                            <span className="flex items-center space-x-1 flex-shrink-0 ml-2">
                              {isCorrectAnswer && isStudentAnswer && <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />}
                              {isStudentAnswer && !isCorrectAnswer && <XCircle className="w-3.5 h-3.5 text-red-400" />}
                              {isCorrectAnswer && !isStudentAnswer && (
                                <span className="text-[10px] text-teal-400/70">correct</span>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Text/Essay/Code */}
                  {!question?.options?.length && answer && (answer.answer || answer.code_answer) && (
                    <div className="bg-white/[0.02] border border-white/[0.05] rounded-md p-3 mb-2">
                      <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1.5">Student's Answer</p>
                      {answer.code_answer ? (
                        <pre className="text-white/75 font-mono text-xs overflow-x-auto whitespace-pre-wrap">{answer.code_answer}</pre>
                      ) : (
                        <p className="text-sm text-white/75">
                          {typeof answer.answer === 'string' ? answer.answer :
                           Array.isArray(answer.answer) ? answer.answer.join(', ') :
                           JSON.stringify(answer.answer)}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Time + grader comments inline */}
                  <div className="flex items-center space-x-4 mt-2">
                    {(answer?.time_taken_seconds || answer?.time_spent_seconds || answer?.time_spent) && (
                      <span className="text-[11px] text-white/25 flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>{Math.floor((answer.time_taken_seconds || answer.time_spent_seconds || answer.time_spent || 0) / 60)}m {(answer.time_taken_seconds || answer.time_spent_seconds || answer.time_spent || 0) % 60}s</span>
                      </span>
                    )}
                    {answer?.grader_comments && (
                      <span className="text-[11px] text-amber-400/60 flex items-center space-x-1">
                        <Award className="w-3 h-3" />
                        <span>{answer.grader_comments}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2 py-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-1.5 rounded-md bg-white/[0.04] border border-white/[0.06] text-white/50 hover:text-white hover:bg-white/[0.08] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`w-8 h-8 rounded-md text-xs font-medium transition-colors ${
                p === page
                  ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30'
                  : 'bg-white/[0.03] border border-white/[0.06] text-white/40 hover:text-white/70 hover:bg-white/[0.06]'
              }`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-1.5 rounded-md bg-white/[0.04] border border-white/[0.06] text-white/50 hover:text-white hover:bg-white/[0.08] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

function calculateGrade(percentage: number): string {
  if (percentage >= 90) return 'A+';
  if (percentage >= 85) return 'A';
  if (percentage >= 80) return 'A-';
  if (percentage >= 75) return 'B+';
  if (percentage >= 70) return 'B';
  if (percentage >= 65) return 'B-';
  if (percentage >= 60) return 'C+';
  if (percentage >= 55) return 'C';
  if (percentage >= 50) return 'D';
  return 'F';
}
